import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Waves, MapPin, Calendar, Shield, Wallet, GraduationCap, BarChart3 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../auth";
import HowItWorks from "../components/HowItWorks";

const FeatureCard = ({ icon: Icon, title, desc, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="card-soft p-6 fade-up text-left w-full hover:-translate-y-0.5 hover:shadow-lg transition-all focus:outline-none focus:ring-4 focus:ring-sky-100"
  >
    <div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center mb-3"><Icon className="w-5 h-5" /></div>
    <h3 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: "Outfit" }}>{title}</h3>
    <p className="text-sm text-slate-600">{desc}</p>
  </button>
);

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();

  const goMap = () => nav("/map");
  const goClientPlanning = () => {
    if (!user) return nav("/login");
    if (user.role === "mns") return nav("/mns/dashboard");
    nav("/dashboard");
  };
  const goCRM = () => {
    if (!user) return nav("/login");
    if (user.role === "mns") return nav("/mns/dashboard");
    return nav("/login");
  };
  const goPayment = () => (user ? nav("/map") : nav("/login"));
  const goProfile = () => (user ? nav("/map") : nav("/login"));

  return (
    <div>
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-12 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="fade-up">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-sky-700 bg-sky-100 px-3 py-1 rounded-full">
            <Waves className="w-3.5 h-3.5" /> Cours de natation à la demande
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 mt-5" style={{ fontFamily: "Outfit" }}>
            Apprenez à nager avec un <span className="text-sky-700">maître-nageur</span> près de chez vous.
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-xl">
            Trouvez des MNS diplômés, consultez leur planning et réservez un créneau en quelques clics. Paiement en ligne ou en espèces, à votre convenance.
          </p>
          <p className="mt-3 text-base font-semibold text-sky-700" data-testid="client-hero-teaser">
            Trouvez un MNS proche de chez vous en 30 secondes ⚡
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/map"><Button data-testid="cta-find-mns" className="rounded-full bg-sky-700 hover:bg-sky-600 px-6 py-6 text-base">Trouver un MNS <MapPin className="w-4 h-4 ml-2" /></Button></Link>
            <Link to="/landing/mns"><Button data-testid="cta-become-mns" variant="outline" className="rounded-full px-6 py-6 text-base border-2">Je suis MNS <GraduationCap className="w-4 h-4 ml-2" /></Button></Link>
          </div>
          <div className="mt-8 flex items-center gap-6 text-sm text-slate-500">
            <span className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-sky-700" /> MNS vérifiés</span>
            <span className="flex items-center gap-1.5"><Wallet className="w-4 h-4 text-sky-700" /> Paiement sécurisé</span>
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-sky-700" /> Réservation immédiate</span>
          </div>
        </div>
        <div className="relative fade-up">
          <div className="absolute -inset-6 bg-gradient-to-br from-sky-200/50 to-teal-200/40 rounded-[2.5rem] blur-2xl" />
          <img
            alt="Nageur dans une eau bleue limpide"
            src="https://images.unsplash.com/photo-1598795289578-8c055891c14c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
            className="relative rounded-[2rem] shadow-2xl object-cover w-full h-[480px]"
          />
          <div className="absolute -bottom-4 -left-4 glass rounded-2xl p-4 shadow-xl flex items-center gap-3">
            <div className="bg-sky-700 text-white p-2 rounded-xl"><Waves className="w-4 h-4" /></div>
            <div>
              <div className="text-xs text-slate-500">Cours du jour</div>
              <div className="text-sm font-semibold text-slate-900">+247 séances réservées</div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-20">
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit" }}>Une expérience fluide, comme l'eau.</h2>
        <p className="text-slate-600 mb-8">Conçue pour les clients comme pour les maîtres-nageurs.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <FeatureCard testId="feat-map" icon={MapPin} title="Carte interactive" desc="Localisez les piscines et MNS proches de vous, avec distances en temps réel." onClick={goMap} />
          <FeatureCard testId="feat-planning" icon={Calendar} title="Planning intelligent" desc="Les MNS dupliquent leur semaine type et vous réservez en un clin d'œil." onClick={goClientPlanning} />
          <FeatureCard testId="feat-mns-cert" icon={Shield} title="MNS certifiés" desc="Chaque MNS s'engage sur la validité de ses diplômes et son appartenance à l'établissement dans lequel les cours seront dispensés." onClick={goMap} />
          <FeatureCard testId="feat-payment" icon={Wallet} title="Paiement flexible" desc="En ligne (Stripe) ou en espèces : vous choisissez, le MNS encaisse." onClick={goPayment} />
          <FeatureCard testId="feat-profile" icon={GraduationCap} title="Profil personnalisé" desc="Indiquez votre niveau, vos objectifs, vos besoins (situation de handicap, peur de l'eau)." onClick={goProfile} />
          <FeatureCard testId="feat-crm" icon={BarChart3} title="CRM pour MNS" desc="Heures, clients, CA, facturation: tous vos chiffres d'un coup d'œil." onClick={goCRM} />
        </div>
      </section>

      <HowItWorks />
    </div>
  );
}
